import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest } from "../../helpers/api";
import AuthenticatedMedia from "../AuthenticatedMedia";
import { Can } from "../AccessContext";
import { showError, showSuccess, showWarning } from "../ToastNotifications";
import { SkeletonCardGrid, SkeletonList, SkeletonRegion } from "../skeleton/Skeleton";
import PostPreview from "./PostPreview";
import StudioDialog from "./StudioDialog";
import {
  formatPostDate, hashtagsToText, isLocked, monthKey, monthTitle, platformIcon, platformName, plainCaption, postStatuses,
  postTitle, shiftMonth, statusLabels, studioPlatforms, studioRoutes, toDateTimeInput
} from "./postStudioUtils";
import "./PostStudio.css";

const schedulePermissions = ["user.posts.schedule", "user.content_calendar.schedule"];
const viewModes = [["grid", "ti-layout-grid", "Grid view"], ["list", "ti-list", "List view"]];

// The chosen layout is a per-browser convenience; storage can be unavailable (private mode), so fail soft.
const readView = (key) => {
  try { return window.localStorage.getItem(key) === "list" ? "list" : "grid"; } catch { return "grid"; }
};

const primaryMedia = (post) => post.media?.[0]
  || (post.imageUrl ? { url: post.imageUrl, mimeType: "image/*" } : post.videoUrl ? { url: post.videoUrl, mimeType: "video/*" } : null);

const MediaThumb = ({ post }) => {
  const primary = primaryMedia(post);
  if (!primary) return <i className="ti ti-photo" aria-hidden="true" />;
  return primary.mimeType?.startsWith("video/") ? <AuthenticatedMedia src={primary.url} video muted /> : <AuthenticatedMedia src={primary.url} alt="" loading="lazy" />;
};

// Listing shared by both studios. `source` scopes every request to AI or manual posts; `actions`
// lists which card actions the studio offers, so each workflow stays independent.
const PostStudioList = ({ source, eyebrow, title, intro, createLabel, createIcon, createPermission, actions, emptyTitle, emptyText }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const routes = studioRoutes[source];
  const [month, setMonth] = useState(monthKey);
  const [allMonths, setAllMonths] = useState(false);
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const viewKey = `postStudioView:${source}`;
  const [view, setView] = useState(() => readView(viewKey));
  const changeView = (next) => {
    setView(next);
    try { window.localStorage.setItem(viewKey, next); } catch { /* preference only */ }
  };

  const loadPosts = useCallback(async (showSkeleton = true) => {
    if (showSkeleton) setLoading(true);
    setLoadError("");
    const query = new URLSearchParams({ source });
    if (!allMonths) query.set("month", month);
    if (platform) query.set("platform", platform);
    if (status) query.set("status", status);
    try {
      const result = await apiRequest(`/api/posts?${query.toString()}`);
      setPosts(result.posts || []);
    } catch (error) {
      setLoadError(error.message || "Unable to load posts.");
    } finally {
      if (showSkeleton) setLoading(false);
    }
  }, [allMonths, month, platform, source, status]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  useEffect(() => {
    let active = true;
    apiRequest("/api/social/accounts")
      .then((result) => { if (active) setAccounts((result.accounts || []).filter((account) => account.status === "CONNECTED")); })
      .catch(() => { if (active) setAccounts([]); });
    return () => { active = false; };
  }, []);

  // Deep links (calendar, notifications, analytics) open a post's preview: ?postId=123
  const linkedPostId = searchParams.get("postId");
  useEffect(() => {
    if (!linkedPostId) return;
    const linked = posts.find((post) => String(post.id) === linkedPostId);
    if (linked) setPreviewing(linked);
    else apiRequest(`/api/posts/${encodeURIComponent(linkedPostId)}`).then((result) => {
      if (result.post?.generationSource === source || (source === "MANUAL" && result.post?.generationSource !== "AI")) setPreviewing(result.post);
    }).catch(() => {});
  }, [linkedPostId, posts, source]);

  const closePreview = useCallback(() => {
    setPreviewing(null);
    if (searchParams.has("postId")) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const accountFor = (post) => accounts.find((account) => account.platform === post.platform);
  const visiblePosts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return posts.filter((post) => !needle || [post.title, post.topic, plainCaption(post), hashtagsToText(post.hashtags), post.platform, post.contentType]
      .some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [posts, search]);
  const hasFilters = Boolean(search || platform || status);

  const run = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      if (successMessage) showSuccess(successMessage);
      return true;
    } catch (error) {
      showError(error.message || "The post could not be updated.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = () => run(async () => {
    await apiRequest(`/api/posts/${deleting.id}`, { method: "DELETE" });
    setPosts((items) => items.filter((post) => post.id !== deleting.id));
    setDeleting(null);
    closePreview();
  }, "Post deleted.");

  const duplicate = (post) => run(async () => {
    await apiRequest(`/api/posts/${post.id}/duplicate`, { method: "POST", body: JSON.stringify({}) });
    await loadPosts(false);
  }, "Post duplicated as a draft.");

  const saveSchedule = (event) => {
    event.preventDefault();
    const when = new Date(scheduling.value);
    if (!scheduling.value || Number.isNaN(when.getTime()) || when <= new Date()) {
      showWarning("Choose a future date and time.");
      return;
    }
    run(async () => {
      await apiRequest(`/api/posts/${scheduling.post.id}/schedule`, { method: "POST", body: JSON.stringify({ scheduledAt: when.toISOString() }) });
      setScheduling(null);
      await loadPosts(false);
    }, "Post scheduled.");
  };

  const confirmPublish = () => run(async () => {
    await apiRequest(`/api/posts/${publishing.id}/publish`, { method: "POST", body: JSON.stringify({}) });
    setPublishing(null);
    await loadPosts(false);
  }, "Post published.");

  const renderActions = (post, variant) => {
    const locked = isLocked(post);
    // Only offer Publish where a connected account has a registered publishing provider.
    const publishable = Boolean(accountFor(post)?.publishingSupported);
    return <>
      {actions.includes("preview") && <button type="button" className="studio-text-button" onClick={() => setPreviewing(post)}><i className="ti ti-eye" aria-hidden="true" /> Preview</button>}
      {actions.includes("edit") && !locked && <Can permission="user.posts.edit"><button type="button" className="studio-text-button" onClick={() => navigate(routes.edit(post.id))}><i className="ti ti-pencil" aria-hidden="true" /> Edit</button></Can>}
      {actions.includes("duplicate") && <Can permission="user.posts.create"><button type="button" className="studio-text-button" disabled={busy} onClick={() => duplicate(post)}><i className="ti ti-copy" aria-hidden="true" /> Duplicate</button></Can>}
      {actions.includes("schedule") && !locked && <Can permission={schedulePermissions}><button type="button" className="studio-text-button" onClick={() => { setPreviewing(null); setScheduling({ post, value: toDateTimeInput(post.scheduledAt) }); }}><i className="ti ti-calendar-time" aria-hidden="true" /> {post.status === "SCHEDULED" ? "Reschedule" : "Schedule"}</button></Can>}
      {actions.includes("publish") && !locked && publishable && <Can permission="user.posts.publish"><button type="button" className="studio-text-button" disabled={busy} onClick={() => { setPreviewing(null); setPublishing(post); }}><i className="ti ti-send" aria-hidden="true" /> Publish</button></Can>}
      {actions.includes("delete") && <Can permission="user.posts.delete"><button type="button" className={`studio-text-button is-danger${variant === "card" ? " is-end" : ""}`} onClick={() => { setPreviewing(null); setDeleting(post); }}><i className="ti ti-trash" aria-hidden="true" /> Delete</button></Can>}
    </>;
  };

  return <section className="studio-page">
    <header className="studio-header">
      <div><p className="studio-eyebrow">{eyebrow}</p><h1>{title}</h1><p>{intro}</p></div>
      <Can permission={createPermission}><Link className="btn btn-primary studio-primary-button" to={routes.create}><i className={`ti ${createIcon}`} aria-hidden="true" /> {createLabel}</Link></Can>
    </header>

    <section className="studio-toolbar" aria-label="Post filters">
      <div className="studio-month">
        <button type="button" className="studio-icon-button" aria-label="Previous month" disabled={allMonths} onClick={() => setMonth((value) => shiftMonth(value, -1))}><i className="ti ti-chevron-left" aria-hidden="true" /></button>
        <h2>{allMonths ? "All months" : monthTitle(month)}</h2>
        <button type="button" className="studio-icon-button" aria-label="Next month" disabled={allMonths} onClick={() => setMonth((value) => shiftMonth(value, 1))}><i className="ti ti-chevron-right" aria-hidden="true" /></button>
        <label className="studio-month-input"><span className="visually-hidden">Calendar month</span><input className="form-control" type="month" value={month} disabled={allMonths} onChange={(event) => event.target.value && setMonth(event.target.value)} /></label>
        <label className="studio-check"><input type="checkbox" checked={allMonths} onChange={(event) => setAllMonths(event.target.checked)} /> All months</label>
      </div>
      <div className="studio-filters">
        <label className="studio-search"><i className="ti ti-search" aria-hidden="true" /><span className="visually-hidden">Search posts</span><input className="form-control" type="search" placeholder="Search title, caption or hashtag" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        <label><span className="visually-hidden">Filter by platform</span><select className="form-select" value={platform} onChange={(event) => setPlatform(event.target.value)}><option value="">All platforms</option>{studioPlatforms.map((item) => <option key={item} value={item}>{platformName(item)}</option>)}</select></label>
        <label><span className="visually-hidden">Filter by status</span><select className="form-select" value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option>{postStatuses.map((item) => <option key={item} value={item}>{statusLabels[item]}</option>)}</select></label>
      </div>
    </section>

    <div className="studio-results">
      <span>{loading ? "Loading posts…" : `${visiblePosts.length} ${visiblePosts.length === 1 ? "post" : "posts"}`}</span>
      <div className="studio-results-end">
        <Link to="/content-calendar"><i className="ti ti-calendar-event" aria-hidden="true" /> Open Content Calendar</Link>
        <div className="studio-view-toggle" role="group" aria-label="Post layout">
          {viewModes.map(([mode, icon, label]) => <button key={mode} type="button" className={view === mode ? "is-active" : ""} aria-pressed={view === mode} aria-label={label} title={label} onClick={() => changeView(mode)}><i className={`ti ${icon}`} aria-hidden="true" /></button>)}
        </div>
      </div>
    </div>

    {loading ? <SkeletonRegion label="Loading posts">{view === "list" ? <div className="studio-list-skeleton"><SkeletonList rows={6} action /></div> : <SkeletonCardGrid count={6} min={260} media lines={3} footer />}</SkeletonRegion>
      : loadError ? <div className="studio-empty" role="alert"><span className="studio-empty-icon is-error"><i className="ti ti-alert-triangle" aria-hidden="true" /></span><h3>Posts could not be loaded</h3><p>{loadError}</p><button type="button" className="btn btn-outline-primary" onClick={() => loadPosts()}>Try again</button></div>
      : visiblePosts.length === 0 ? <div className="studio-empty"><span className="studio-empty-icon"><i className={`ti ${createIcon}`} aria-hidden="true" /></span><h3>{hasFilters ? "No matching posts" : emptyTitle}</h3><p>{hasFilters ? "Adjust your search or filters to see more posts." : emptyText}</p>{!hasFilters && <Can permission={createPermission}><Link className="btn btn-outline-primary" to={routes.create}>{createLabel}</Link></Can>}</div>
      : view === "list" ? <ul className="studio-list">{visiblePosts.map((post) => <li className="studio-row" key={post.id}>
        <button type="button" className="studio-row-media" onClick={() => setPreviewing(post)} aria-label={`Preview ${postTitle(post)}`}>
          <MediaThumb post={post} />
          {post.media?.length > 1 && <span className="studio-card-count">{post.media.length}</span>}
        </button>
        <div className="studio-row-main">
          <h3>{postTitle(post)}</h3>
          <p className="studio-row-caption">{plainCaption(post) || hashtagsToText(post.hashtags)}</p>
          {post.status === "FAILED" && post.errorMessage && <p className="studio-card-error"><i className="ti ti-alert-circle" aria-hidden="true" /> {post.errorMessage}</p>}
        </div>
        <span className="studio-platform studio-row-platform"><i className={`ti ${platformIcon(post.platform)}`} aria-hidden="true" /> {platformName(post.platform)}</span>
        <span className={`studio-status status-${String(post.status || "DRAFT").toLowerCase()}`}>{statusLabels[post.status] || post.status}</span>
        <span className="studio-row-date"><i className="ti ti-clock" aria-hidden="true" /> {formatPostDate(post.scheduledAt)}{post.contentType ? <small>{post.contentType}</small> : null}</span>
        <div className="studio-row-actions">{renderActions(post, "row")}</div>
      </li>)}</ul>
      : <div className="studio-grid">{visiblePosts.map((post) => {
        return <article className="studio-card" key={post.id}>
          <button type="button" className="studio-card-media" onClick={() => setPreviewing(post)} aria-label={`Preview ${postTitle(post)}`}>
            <MediaThumb post={post} />
            {post.media?.length > 1 && <span className="studio-card-count">{post.media.length}</span>}
          </button>
          <div className="studio-card-body">
            <div className="studio-card-labels"><span className="studio-platform"><i className={`ti ${platformIcon(post.platform)}`} aria-hidden="true" /> {platformName(post.platform)}</span><span className={`studio-status status-${String(post.status || "DRAFT").toLowerCase()}`}>{statusLabels[post.status] || post.status}</span></div>
            <h3>{postTitle(post)}</h3>
            <p className="studio-card-caption">{plainCaption(post)}</p>
            {post.hashtags?.length > 0 && <p className="studio-card-hashtags">{hashtagsToText(post.hashtags)}</p>}
            <p className="studio-card-date"><i className="ti ti-clock" aria-hidden="true" /> {formatPostDate(post.scheduledAt)}{post.contentType ? ` · ${post.contentType}` : ""}</p>
            {post.status === "FAILED" && post.errorMessage && <p className="studio-card-error"><i className="ti ti-alert-circle" aria-hidden="true" /> {post.errorMessage}</p>}
          </div>
          <footer className="studio-card-actions">{renderActions(post, "card")}</footer>
        </article>;
      })}</div>}

    {previewing && <StudioDialog eyebrow={`${platformName(previewing.platform)} · ${statusLabels[previewing.status] || previewing.status}`} title={postTitle(previewing)} onClose={closePreview}
      actions={<div className="studio-dialog-action-row">{renderActions(previewing, "dialog")}</div>}>
      <PostPreview post={previewing} accountName={accountFor(previewing)?.accountName || accountFor(previewing)?.username} richText={source === "MANUAL"} />
    </StudioDialog>}

    {deleting && <StudioDialog role="alertdialog" eyebrow="REMOVE POST" title="Delete this post?" busy={busy} onClose={() => setDeleting(null)}
      actions={<><button type="button" className="btn btn-light" disabled={busy} onClick={() => setDeleting(null)}>Cancel</button><button type="button" className="btn btn-danger" disabled={busy} onClick={confirmDelete}>{busy ? "Deleting…" : "Delete post"}</button></>}>
      <p className="studio-dialog-copy">“{postTitle(deleting)}” will be removed from {title} and the Content Calendar. This can't be undone.</p>
    </StudioDialog>}

    {scheduling && <StudioDialog eyebrow="SCHEDULE" title={scheduling.post.status === "SCHEDULED" ? "Reschedule post" : "Schedule post"} busy={busy} onClose={() => setScheduling(null)}>
      <form className="studio-dialog-form" onSubmit={saveSchedule}>
        <label className="studio-field"><span>Publish date and time</span><input className="form-control" type="datetime-local" min={toDateTimeInput(new Date())} value={scheduling.value} onChange={(event) => setScheduling({ ...scheduling, value: event.target.value })} required /></label>
        {!accountFor(scheduling.post) && <p className="studio-hint is-warning"><i className="ti ti-plug-connected-x" aria-hidden="true" /> No connected {platformName(scheduling.post.platform)} account — the post will fail at publish time unless you <Link to="/social-accounts">connect one</Link>.</p>}
        <div className="studio-dialog-actions is-inline"><button type="button" className="btn btn-light" disabled={busy} onClick={() => setScheduling(null)}>Cancel</button><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Save schedule"}</button></div>
      </form>
    </StudioDialog>}

    {publishing && <StudioDialog role="alertdialog" eyebrow="PUBLISH NOW" title="Publish this post now?" busy={busy} onClose={() => setPublishing(null)}
      actions={<><button type="button" className="btn btn-light" disabled={busy} onClick={() => setPublishing(null)}>Cancel</button><button type="button" className="btn btn-primary" disabled={busy} onClick={confirmPublish}>{busy ? "Publishing…" : "Publish now"}</button></>}>
      <p className="studio-dialog-copy">“{postTitle(publishing)}” will be posted to {accountFor(publishing)?.accountName || accountFor(publishing)?.username || platformName(publishing.platform)} immediately.</p>
    </StudioDialog>}
  </section>;
};

export default PostStudioList;
